/**
 * Recently viewed row.
 *
 * Fills `sections/recently-viewed.liquid` from the visitor's browsing history.
 * Handles are written to localStorage by `add-to-recently-viewed.js` on every
 * product page view; Liquid cannot read localStorage, so the section ships an
 * empty slider and this fetches the cards client-side.
 *
 * Each card comes from `/products/<handle>?view=recently-viewed-bestseller-card`,
 * an alternate product template that renders one `wt-bestsellers__card` matching
 * the "You may also like" row.
 *
 * This is a sibling of `recently-viewed-products.js`, which does the same job for
 * the older `wt-featured-collection`-styled section. Kept separate rather than
 * parameterised because that file is vendor code the theme may update.
 */

const CACHE_TTL = 300000; // 5 minutes
const CARD_VIEW = 'recently-viewed-bestseller-card';
const CACHE_PREFIX = 'rvbc_';

/** In-memory cache, shared across instances, so one handle is fetched once per page. */
const CARD_CACHE = new Map();

/**
 * Fetch one product card as an HTML string.
 * Returns '' on any failure — a missing card is skipped, never fatal.
 */
async function fetchCard(handle) {
  const now = Date.now();

  const memCached = CARD_CACHE.get(handle);
  if (memCached && now < memCached.expiry) return memCached.html;

  // sessionStorage survives navigation within the tab, so moving between pages
  // does not re-request cards already seen this session.
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + handle);
    if (raw) {
      const cached = JSON.parse(raw);
      if (now < cached.expiry) {
        CARD_CACHE.set(handle, cached);
        return cached.html;
      }
      sessionStorage.removeItem(CACHE_PREFIX + handle);
    }
  } catch (e) {
    // Malformed entry, or storage blocked (Safari private browsing). Fall through
    // to the network — the cache is an optimisation, not a requirement.
  }

  try {
    const response = await fetch(`/products/${encodeURIComponent(handle)}?view=${CARD_VIEW}`);
    if (!response.ok) return '';

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const html = doc.querySelector('[data-swiper-slide]')?.outerHTML || '';

    if (html) {
      const entry = { html, expiry: now + CACHE_TTL };
      CARD_CACHE.set(handle, entry);
      try {
        sessionStorage.setItem(CACHE_PREFIX + handle, JSON.stringify(entry));
      } catch (e) {
        // Quota exceeded or storage disabled — in-memory cache still applies.
      }
    }
    return html;
  } catch (e) {
    return '';
  }
}

/** Reject a promise after `ms`, so a missing slider component cannot hang the row. */
function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms)),
  ]);
}

if (!customElements.get('recently-viewed-row')) {
  customElements.define(
    'recently-viewed-row',
    class RecentlyViewedRow extends HTMLElement {
      connectedCallback() {
        // In the theme editor the section renders placeholder cards from Liquid.
        // Leave them alone — the merchant needs to see the row while styling it,
        // and their own browsing history is not representative of a shopper's.
        if (this.hasAttribute('data-design-mode')) return;

        // Defer the work until the row is near the viewport. It sits above the
        // footer, so on most visits it is never reached and never fetches.
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              this.observer.disconnect();
              this.init();
            }
          },
          { rootMargin: '200px' }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      /** The visitor's history, newest first, minus the product being viewed. */
      getHandles() {
        let list;
        try {
          list = JSON.parse(localStorage.getItem(`recently_viewed_handles_${this.dataset.shopId}`));
        } catch (e) {
          return [];
        }
        if (!Array.isArray(list)) return [];

        // `data-current-handle` is blank on non-product templates such as the
        // shop page, in which case nothing is excluded.
        const current = this.dataset.currentHandle;
        const filtered = current ? list.filter((handle) => handle !== current) : list;

        return filtered
          .filter((handle) => typeof handle === 'string' && handle)
          .slice(0, parseInt(this.dataset.productsToShow, 10) || 5);
      }

      hideSection() {
        if (this.hasAttribute('data-hide-empty-section')) {
          this.closest('.wt-shopify-section, section')?.classList.add('hidden');
        }
      }

      async init() {
        const handles = this.getHandles();
        if (handles.length === 0) {
          this.hideSection();
          return;
        }

        const cards = (await Promise.all(handles.map(fetchCard))).filter(Boolean);

        // Every card failed to load — a deleted or unpublished product, or the
        // network is down. An empty row is worse than no row.
        if (cards.length === 0) {
          this.hideSection();
          return;
        }

        const track = this.querySelector('[data-swiper-container]');
        if (!track) return;
        track.innerHTML = cards.join('');

        // Slides were injected after `slider.js` initialised, so the Swiper
        // instance has to be rebuilt around them.
        try {
          await withTimeout(customElements.whenDefined('slideshow-section'), 3000);
          const slideshow = this.querySelector('slideshow-section');
          if (typeof slideshow?.swiperInitilize === 'function') {
            if (typeof slideshow.swiperDestroy === 'function') slideshow.swiperDestroy();
            slideshow.swiperInitilize();
          }
        } catch (e) {
          // Slider never arrived. The cards are in the DOM and readable as a
          // plain overflow row, so leave them visible rather than hiding.
          this.querySelector('[data-swiper]')?.classList.remove('loading');
        }
      }
    }
  );
}
